/**
 * Standardisiert ein Instagram-Profil zu einem einheitlichen Format.
 * Verarbeitet verschiedene Eingabeformate (URLs, Benutzernamen mit @, etc.) und gibt
 * einen standardisierten Instagram-Benutzernamen und eine entsprechende URL zurück.
 * 
 * @param {string} profileValue - Der Wert des Instagram-Profils (URL, Benutzername, etc.)
 * @returns {Object} - Ein Objekt mit { username, profile_url } oder leere Strings, wenn keine gültige Eingabe
 */
export const standardizeInstagramProfile = (profileValue) => {
  if (!profileValue) return { username: '', profile_url: '' };
  
  // Entferne Leerzeichen am Anfang und Ende
  let profile = profileValue.trim();
  
  // Entferne @ am Anfang, falls vorhanden
  if (profile.startsWith('@')) {
    profile = profile.substring(1);
  }
  
  // Wenn es eine URL ist, extrahiere den Benutzernamen
  let username = profile;
  
  // Verarbeite verschiedene URL-Formate
  const instagramUrlPatterns = [
    /https?:\/\/(?:www\.)?instagram\.com\/([A-Za-z0-9._]+)\/?/,    // https://www.instagram.com/username/
    /https?:\/\/instagram\.com\/([A-Za-z0-9._]+)\/?/,               // https://instagram.com/username/
    /(?:www\.)?instagram\.com\/([A-Za-z0-9._]+)\/?/                 // instagram.com/username/ or www.instagram.com/username/
  ];
  
  for (const pattern of instagramUrlPatterns) {
    const match = profile.match(pattern);
    if (match && match[1]) {
      username = match[1];
      break;
    }
  }
  
  // Entferne Parameter oder Fragmente aus der URL
  if (username.includes('?')) {
    username = username.split('?')[0];
  }
  if (username.includes('#')) {
    username = username.split('#')[0];
  }
  
  // Entferne unerlaubte Zeichen (nur Buchstaben, Zahlen, Punkte und Unterstriche sind in Instagram-Benutzernamen erlaubt)
  username = username.replace(/[^A-Za-z0-9._]/g, '');
  
  // Erstelle die standardisierte URL
  const standardizedUrl = `https://instagram.com/${username}`;
  
  return {
    username,
    profile_url: standardizedUrl
  };
};

// Ein Cache für kürzlich hinzugefügte Profile, um mehrfache Hinzufügungen zu verhindern
const recentlyAddedProfiles = new Map();

/**
 * Prüft, ob ein Instagram-Profil kürzlich hinzugefügt wurde, und markiert es als hinzugefügt.
 * Verhindert mehrfache Hinzufügungen innerhalb eines kurzen Zeitraums.
 * 
 * @param {string} username - Der normalisierte Instagram-Benutzername
 * @returns {boolean} - True, wenn das Profil bereits kürzlich hinzugefügt wurde, sonst False
 */
export const isRecentlyAdded = (username) => {
  if (!username) return false;
  
  const now = Date.now();
  
  // Profile werden für 10 Sekunden im Cache gehalten, um mehrfache Hinzufügungen zu verhindern
  const CACHE_DURATION = 10000; // 10 Sekunden
  
  // Bereinige den Cache von alten Einträgen
  for (const [key, timestamp] of recentlyAddedProfiles.entries()) {
    if (now - timestamp > CACHE_DURATION) {
      recentlyAddedProfiles.delete(key);
    }
  }
  
  // Prüfe, ob das Profil kürzlich hinzugefügt wurde
  if (recentlyAddedProfiles.has(username)) {
    return true;
  }
  
  // Markiere das Profil als hinzugefügt
  recentlyAddedProfiles.set(username, now);
  return false;
};

/**
 * Erstellt eine Debounce-Funktion, die verhindert, dass eine Funktion zu oft aufgerufen wird.
 * 
 * @param {Function} func - Die Funktion, die gedebounced werden soll
 * @param {number} wait - Die Wartezeit in Millisekunden
 * @returns {Function} - Die gedebounce-Funktion
 */
export const debounce = (func, wait) => {
  let timeout;
  
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}; 